import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import { MerchantCashAdvance, MerchantCashAdvanceStatus, useMerchantCashAdvancesQuery } from '../../graphql/types';
import { Autocomplete, TextField } from '@mui/material';
import AssetLogo from '../AssetLogo';
import McaListButtonOption from './McaListButtonOption';
import { debounce } from 'lodash';

export interface Props {
  setMca: (value: ((prevState: MerchantCashAdvance | null) => MerchantCashAdvance | null) | MerchantCashAdvance | null) => void;
  initialValue?: MerchantCashAdvance | null;
}

export const McaAutocomplete = ({ setMca, initialValue }: Props) => {
  const [mcas, setMerchantCashAdvances] = useState<readonly MerchantCashAdvance[]>(initialValue ? [initialValue] : []);
  const [mca, setCurrentMca] = useState<MerchantCashAdvance | null>(initialValue || null);
  const inputReference = useRef<HTMLInputElement>(null);
  const {
    loading: mcasLoading,
    data: mcasData,
    refetch: mcasRefetch
  } = useMerchantCashAdvancesQuery({
    variables: {
      status: Object.values(MerchantCashAdvanceStatus).filter(
        (status) =>
          ![MerchantCashAdvanceStatus.WrittenOff, MerchantCashAdvanceStatus.Completed, MerchantCashAdvanceStatus.Settled].includes(status)
      ),
      sortArgs: [{ field: 'balanceclr', direction: 'desc' }]
    },
    fetchPolicy: 'network-only'
  });
  const mcasFetch = useMemo(
    () =>
      debounce(
        (request: { input: string }) =>
          mcasRefetch({ search: request.input }).then((rq) =>
            setMerchantCashAdvances((rq.data.merchantCashAdvances.data as MerchantCashAdvance[]) || [])
          ),
        200
      ),
    [mcasRefetch]
  );
  const [mcaInputValue, setMerchantCashAdvanceInputValue] = useState<string>('');
  useEffect(() => {
    if (!mcaInputValue) {
      const fetched = (mcasData?.merchantCashAdvances.data as MerchantCashAdvance[]) || [];
      if (initialValue && !fetched.some((m) => m.id === initialValue.id)) {
        setMerchantCashAdvances([initialValue, ...fetched]);
      } else {
        setMerchantCashAdvances(fetched);
      }
    }
  }, [mcasData?.merchantCashAdvances.data, mcasLoading, mcaInputValue, initialValue]);
  useEffect(() => {
    mcasFetch({ input: mcaInputValue });
  }, [mcaInputValue, mcasFetch]);
  useEffect(() => {
    setMca(mca);
  }, [mca]);

  return (
    <Autocomplete
      getOptionLabel={(option: MerchantCashAdvance) => option.number + ' ' + option?.merchant?.legalName}
      options={mcas}
      filterOptions={(xs) => xs as any}
      autoComplete
      value={mca}
      isOptionEqualToValue={(option: MerchantCashAdvance, value: MerchantCashAdvance) => option.id === value.id}
      onChange={(event: SyntheticEvent, newValue: MerchantCashAdvance | null) => {
        setCurrentMca(newValue);
      }}
      onInputChange={(event: SyntheticEvent, newInputValue: string) => {
        setMerchantCashAdvanceInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder={'Select MCA'}
          inputRef={inputReference}
          InputProps={{
            ...params.InputProps,
            startAdornment: mca?.merchant?.id && <AssetLogo logo={mca?.merchant?.logo} name={mca?.merchant?.legalName} size={'sm'} />
          }}
          sx={{
            '& fieldset': { border: 'none' },
            '& .MuiInputLabel-root': { padding: '18px 0 12px 16px' },
            '& .MuiOutlinedInput-root': { padding: '18px 0 12px 16px' }
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          <McaListButtonOption mca={option} />
        </li>
      )}
    />
  );
};
